import React, { useState, useEffect, useReducer, useMemo, useRef } from 'react';
import { useTable, useFilters, useGlobalFilter, useSortBy, useExpanded, usePagination, useRowSelect, useFlexLayout } from 'react-table';
import defaults from 'lodash/defaults';
import transform from 'lodash/transform';
import isEqual from 'lodash/isEqual';
import { useHistory, useLocation } from 'react-router-dom';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import omit from 'lodash/omit';

function toVal(mix) {
	var k, y, str='';

	if (typeof mix === 'string' || typeof mix === 'number') {
		str += mix;
	} else if (typeof mix === 'object') {
		if (Array.isArray(mix)) {
			for (k=0; k < mix.length; k++) {
				if (mix[k]) {
					if (y = toVal(mix[k])) {
						str && (str += ' ');
						str += y;
					}
				}
			}
		} else {
			for (k in mix) {
				if (mix[k]) {
					str && (str += ' ');
					str += k;
				}
			}
		}
	}

	return str;
}

function clsx () {
	var i=0, tmp, x, str='';
	while (i < arguments.length) {
		if (tmp = arguments[i++]) {
			if (x = toVal(tmp)) {
				str && (str += ' ');
				str += x;
			}
		}
	}
	return str;
}

const configContext = React.createContext(undefined);
function useTabuliskConfig() {
    return React.useContext(configContext);
}
function TabuliskConfigProvider({ children, config = {} }) {
    return React.createElement(configContext.Provider, { value: config }, children);
}

const iconStyle = { width: '1rem', height: '1rem', display: 'inline-block', marginLeft: '2px' };
const ChevronDown = () => (React.createElement("span", { style: iconStyle },
    React.createElement("svg", { fill: "none", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", viewBox: "0 0 24 24", stroke: "currentColor" },
        React.createElement("path", { d: "M19 9l-7 7-7-7" }))));
const ChevronUp = () => (React.createElement("span", { style: iconStyle },
    React.createElement("svg", { fill: "none", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", viewBox: "0 0 24 24", stroke: "currentColor" },
        React.createElement("path", { d: "M5 15l7-7 7 7" }))));
const ChevronLeft = () => (React.createElement("svg", { style: { width: '1.25rem', height: '1.25rem' }, fill: "none", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", viewBox: "0 0 24 24", stroke: "currentColor" },
    React.createElement("path", { d: "M15 19l-7-7 7-7" })));
const ChevronRight = () => (React.createElement("svg", { style: { width: '1.25rem', height: '1.25rem' }, fill: "none", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", viewBox: "0 0 24 24", stroke: "currentColor" },
    React.createElement("path", { d: "M9 5l7 7-7 7" })));

const range = (start, end) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, i) => start + i);
};
function PaginatorInfo({ page, pageCount, total }) {
    return (React.createElement(React.Fragment, null,
        "Page ",
        page,
        " of ",
        pageCount,
        " (",
        total,
        " rows total)"));
}
function Paginator({ boundaryCount = 1, classNames = {}, gotoPage, hideNextButton = false, hidePrevButton = false, minimal = false, onChangePageSize = () => { }, pageCount, pageIndex, pageSize, pageSizeOptions = [10, 20, 50, 100], PaginatorInfoComponent = PaginatorInfo, selectorPageLabel = 'Page', showFirstButton = false, showLastButton = false, showPageSizeSelector = false, siblingCount = 2, total, }) {
    const page = pageIndex + 1;
    let itemsList = [];
    if (minimal) {
        const prevMod = page === pageCount ? 2 : 1;
        const nextMod = page === 1 ? 2 : 1;
        itemsList = [
            'first',
            ...range(Math.max(page - prevMod, 1), Math.min(page + nextMod, pageCount)),
            'last',
        ];
    }
    else {
        const startPages = range(1, Math.min(boundaryCount, pageCount));
        const endPages = range(Math.max(pageCount - boundaryCount + 1, boundaryCount + 1), pageCount);
        const siblingsStart = Math.max(Math.min(
        // Natural start
        page - siblingCount, 
        // Lower boundary when page is high
        pageCount - boundaryCount - siblingCount * 2 - 1), 
        // Greater than startPages
        boundaryCount + 2);
        const siblingsEnd = Math.min(Math.max(
        // Natural end
        page + siblingCount, 
        // Upper boundary when page is low
        boundaryCount + siblingCount * 2 + 2), 
        // Less than endPages
        endPages[0] - 2);
        itemsList = [
            ...(showFirstButton ? ['first'] : []),
            ...(hidePrevButton ? [] : ['previous']),
            ...startPages,
            // Start ellipsisaa
            ...(siblingsStart > boundaryCount + 2
                ? ['start-ellipsis']
                : boundaryCount + 1 < pageCount - boundaryCount
                    ? [boundaryCount + 1]
                    : []),
            // Sibling pages
            ...range(siblingsStart, siblingsEnd),
            // End ellipsis
            ...(siblingsEnd < pageCount - boundaryCount - 1
                ? ['end-ellipsis']
                : pageCount - boundaryCount > boundaryCount
                    ? [pageCount - boundaryCount]
                    : []),
            ...endPages,
            ...(hideNextButton ? [] : ['next']),
            ...(showLastButton ? ['last'] : []),
        ];
    }
    const buttonPage = (type) => {
        switch (type) {
            case 'first':
                return 0;
            case 'previous':
                return pageIndex - 1;
            case 'next':
                return pageIndex + 1;
            case 'last':
                return pageCount - 1;
            default:
                return 1;
        }
    };
    return (React.createElement("div", { className: clsx('--tabulisk-paginator-wrapper', classNames.paginatorWrapperClassName) },
        React.createElement("nav", { className: clsx('--tabulisk-paginator', classNames.paginatorClassName) }, pageCount > 1 &&
            itemsList.map((item) => {
                const fn = typeof item === 'number'
                    ? () => gotoPage(item - 1)
                    : () => gotoPage(buttonPage(item));
                const disabled = typeof item === 'number'
                    ? false
                    : item.indexOf('ellipsis') !== -1 ||
                        (item === 'next' || item === 'last'
                            ? page >= pageCount
                            : page <= 1);
                const isCurrent = item === page;
                let btnContent = item;
                switch (item) {
                    case 'first':
                        btnContent = '<<';
                        break;
                    case 'last':
                        btnContent = '>>';
                        break;
                    case 'previous':
                        btnContent = React.createElement(ChevronLeft, null);
                        break;
                    case 'next':
                        btnContent = React.createElement(ChevronRight, null);
                        break;
                    case 'end-ellipsis':
                    case 'start-ellipsis':
                        btnContent = '...';
                        break;
                    default:
                        btnContent = item;
                }
                return (React.createElement("a", { key: item, "aria-current": isCurrent ? 'true' : undefined, onClick: disabled ? () => { } : fn, className: clsx('--tabulisk-paginator-item', classNames.paginatorItemClassName, {
                        '--tabulisk-paginator-item-active': isCurrent,
                        '--tabulisk-paginator-item-disabled': disabled,
                    }) }, btnContent));
            })),
        showPageSizeSelector && (React.createElement("select", { className: clsx('--tabulisk-paginator-pageSizeSelector', classNames.paginatorPageSizeSelectorClassName), value: pageSize, onChange: (e) => onChangePageSize(e.target.value) }, pageSizeOptions.map((opt) => {
            return (React.createElement("option", { key: opt, value: opt },
                opt,
                " / ",
                selectorPageLabel));
        }))),
        React.createElement("span", { className: clsx('--tabulisk-paginator-info', classNames.paginatorInfoClassName) },
            React.createElement(PaginatorInfoComponent, { page: page, pageCount: pageCount, total: total }))));
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const IndeterminateCheckbox = React.forwardRef((_a, ref) => {
    var { indeterminate } = _a, rest = __rest(_a, ["indeterminate"]);
    const defaultRef = React.useRef(null);
    const resolvedRef = ref || defaultRef;
    React.useEffect(() => {
        // @ts-ignore
        resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);
    return (React.createElement(React.Fragment, null,
        React.createElement("input", Object.assign({ type: "checkbox", ref: resolvedRef }, rest))));
});
IndeterminateCheckbox.displayName = 'IndeterminateCheckbox';

// import { scrollbarWidth } from './utils';
const defaultPropGetter = () => ({});
const DEFAULT_CONFIG = {
    classNames: {},
    debug: false,
    disableMultiSort: false,
    filters: {},
    flexDefaultColumnGrow: 100,
    fullWidth: false,
    expanderColumnStyle: { width: 35 },
    selectColumnStyle: { width: 35 },
    getCellProps: defaultPropGetter,
    getColumnProps: defaultPropGetter,
    getHeaderProps: defaultPropGetter,
    getRowProps: defaultPropGetter,
    hiddenColumns: [],
    idPropertyName: 'id',
    manualFilters: false,
    manualPagination: false,
    manualSortBy: false,
    noRowsMessage: 'No results to show',
    pageSize: 20,
    paginate: true,
    remote: false,
    selectable: false,
    showPageSizeSelector: true,
    skipAutoReset: false,
    sorting: true,
    toggleAllSelectOnlyOnCurrentPage: true,
    useEditable: false,
    useExpanded: false,
    useFlex: false,
    useReducer: null,
    paginatorPageSizeOptions: [10, 20, 50, 100],
};
// (function addScrollBarWidthStyle() {
//     const scrollWidth = scrollbarWidth();
//     const style = document.createElement('style');
//     style.textContent = `
//         .--tabulisk-table-flex div[role="columnheader"]:last-child {
//             margin-right: ${scrollWidth}px;
//         }
//     `;
//     document.head.append(style);
// })();
function DataTableComponent({ columns, data, config }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    if (config.useReducer) {
        if (!config.onChangePage) {
            config.onChangePage = config.useReducer.actions.setPage;
        }
        if (!config.onChangePageSize) {
            config.onChangePageSize = config.useReducer.actions.setPageSize;
        }
        if (!config.onToggleSortBy) {
            config.onToggleSortBy = config.useReducer.actions.toggleSortBy;
        }
        if (!config.globalFilter) {
            config.globalFilter = config.useReducer.state.globalFilter;
        }
        if (!config.filters) {
            config.filters = config.useReducer.state.filters;
        }
        if (!config.pageIndex) {
            config.pageIndex = config.useReducer.state.page - 1;
        }
        if (!config.pageSize) {
            config.pageSize = config.useReducer.state.pageSize;
        }
    }
    if (config.remote === true) {
        config.manualFilters = true;
        config.manualPagination = true;
        config.manualSortBy = true;
    }
    const contextConfig = useTabuliskConfig();
    defaults(config, contextConfig, DEFAULT_CONFIG);
    // console.log(config);
    const [tableData, setData] = useState(data);
    const [autoReset, setAutoReset] = useState(!config.skipAutoReset);
    const computedAutoReset = config.useEditable ? autoReset : !config.skipAutoReset;
    // console.log('AUTORESET:', computedAutoReset);
    const initialFilters = React.useMemo(() => {
        const filtersArray = [];
        Object.entries(config.filters).forEach(([k, v]) => {
            if (v !== false && v !== null && v !== '') {
                filtersArray.push({
                    id: k,
                    value: v,
                });
            }
        });
        return filtersArray;
        /* eslint-disable-next-line */
    }, []);
    // const [appliedFilters, setAppliedFilters] = useState(config.filters);
    const processedColumns = React.useMemo(() => {
        const properColumns = columns.map((c) => {
            if (c.fixedWidth) {
                c.width = c.fixedWidth;
            }
            // Se non definito dalla colonna imposto la larghezza minima della colonna a quella della colonna, se non mi trovo in un layout full width
            if (config.fullWidth === false && typeof c.minWidth === 'undefined') {
                c.minWidth = c.width || config.flexDefaultColumnGrow;
            }
            return c;
        });
        return properColumns;
    }, [columns, config.fullWidth, config.flexDefaultColumnGrow]);
    const tableConfig = {
        columns: processedColumns,
        data: config.useEditable ? tableData : data,
        initialState: {
            pageIndex: config.pageIndex ? config.pageIndex : 0,
            pageSize: config.pageSize,
            hiddenColumns: config.hiddenColumns,
            filters: initialFilters,
        },
        manualFilters: config.manualFilters,
        manualSortBy: config.manualSortBy,
        manualGlobalFilter: config.manualFilters,
        autoResetSelectedRows: false,
        autoResetFilters: computedAutoReset,
        autoResetGlobalFilter: computedAutoReset,
        stateReducer: (next, action, prev) => {
            if (config.debug) {
                /* eslint-disable-next-line */
                console.log(action);
            }
            const { type } = action;
            if (type === 'gotoPage') {
                if (config.onChangePage) {
                    const newPageIndex = typeof action.pageIndex === 'function'
                        ? action.pageIndex(prev.pageIndex + 1)
                        : action.pageIndex;
                    setTimeout(() => {
                        if (config.onChangePage) {
                            config.onChangePage(newPageIndex);
                        }
                    }, 0);
                }
                if (config.manualPagination) {
                    return prev;
                }
            }
            if (type === 'setPageSize') {
                setTimeout(() => {
                    if (config.onChangePageSize) {
                        config.onChangePageSize(action.pageSize);
                    }
                }, 0);
                if (config.manualPagination) {
                    return prev;
                }
            }
            if (type === 'toggleSortBy') {
                setTimeout(() => {
                    if (config.onToggleSortBy) {
                        config.onToggleSortBy(action.columnId, action.multi);
                    }
                }, 0);
                if (config.manualSortBy) {
                    return prev;
                }
            }
            if (type === 'toggleRowSelected' ||
                type === 'toggleAllRowsSelected' ||
                type === 'toggleAllPageRowsSelected') {
                if (config.onChangeSelected) {
                    config.onChangeSelected(Object.keys(next.selectedRowIds), {
                        value: action.value,
                        id: action.id,
                    });
                }
            }
            if (type === 'gotoPage' || type === 'resetPage') {
                setTimeout(() => {
                    if (config.onDataRefresh) {
                        config.onDataRefresh();
                    }
                }, 0);
            }
            // console.log(isEqual(prev, next));
            // console.log(prev);
            // console.log(next);
            if (isEqual(prev, next)) {
                return prev;
            }
            return next;
        },
    };
    if (config.useReducer) {
        const state = config.useReducer.state;
        tableConfig.useControlledState = (tableState) => {
            // Dato che sortBy è un oggetto è necessario memorizzare il risultato altrimenti risulta sempre diverso
            // ad ogni render causando un loop
            /* eslint-disable-next-line */
            const sortBy = React.useMemo(() => {
                return state.sortBy;
                // return state.sortBy.map((sortItem: string) => {
                //     const [id, dir] = sortItem.split('::');
                //     return {
                //         id,
                //         desc: dir === 'desc'
                //     };
                // });
            }, 
            /* eslint-disable-next-line */
            [state.sortBy]);
            // console.log(tableState);
            // console.log(state);
            /* eslint-disable-next-line */
            const filters = React.useMemo(() => {
                return transform(state.filters, (filtersArray, value, key) => {
                    if (value !== null) {
                        filtersArray.push({ id: key, value });
                    }
                    return filtersArray;
                }, []);
            }, 
            /* eslint-disable-next-line */
            [state.filters]);
            /* eslint-disable-next-line */
            return React.useMemo(() => {
                return Object.assign(Object.assign({}, tableState), { 
                    // FIXME: riporto qui sempre il globalFilter del reducer in maniera che un action di tipo reset locale non lo sovrascriva
                    // gli altri elementi come pageSize, pageIndex e sortBy pure sono ripresi dallo stato del reducer, l'unica cosa
                    // che non mi spiego è come mai i filtri vengano mantenuti dopo il reset, probabilmente c'è qualche hook che li tiene in
                    // sincronia, indagare...
                    filters, globalFilter: state.globalFilter, pageIndex: state.page - 1, pageSize: state.pageSize, sortBy });
                /* eslint-disable-next-line */
            }, [tableState, state.page, state.pageSize, sortBy]);
        };
    }
    if (config.controlledStateHook) {
        tableConfig.useControlledState = config.controlledStateHook;
    }
    if (config.filterTypes) {
        tableConfig.filterTypes = config.filterTypes;
    }
    if (config.paginate) {
        tableConfig.manualPagination =
            config.manualPagination === undefined ? false : config.manualPagination;
        tableConfig.autoResetPage = computedAutoReset;
    }
    if (config.manualPagination && config.total && config.pageSize) {
        tableConfig.pageCount = Math.ceil(config.total / config.pageSize);
    }
    if (config.sorting) {
        tableConfig.autoResetSortBy = computedAutoReset;
        tableConfig.disableMultiSort = config.disableMultiSort;
        if (config.initialSortBy) {
            tableConfig.initialState.sortBy = config.initialSortBy;
        }
    }
    if (config.sortTypes) {
        tableConfig.sortTypes = config.sortTypes;
    }
    if (config.useEditable) {
        tableConfig.updateData = async (id, columnId, value) => {
            if (!config.updateDataFn) {
                return;
            }
            const res = await config.updateDataFn(id, columnId, value);
            if (res === false) {
                return false;
            }
            setAutoReset(false);
            setData((old) => old.map((row) => {
                if (String(id) === String(row[config.idPropertyName || 'id']) && res) {
                    return res;
                }
                return row;
            }));
            return true;
        };
    }
    if (config.useExpanded) {
        tableConfig.autoResetExpanded = computedAutoReset;
    }
    // Gestisco eventuali modifiche esterne ai dati
    React.useEffect(() => {
        if (config.useEditable) {
            setTimeout(() => setData(data), 0);
        }
    }, [data, config.useEditable]);
    React.useEffect(() => {
        if (config.useEditable) {
            setAutoReset(!config.skipAutoReset);
        }
    }, [config.skipAutoReset, config.useEditable]);
    // Ripristino l'autoreset dopo aver aggiornato i dati internamente
    React.useEffect(() => {
        if (config.useEditable) {
            setAutoReset(!config.skipAutoReset);
        }
    }, [tableData, config.useEditable, config.skipAutoReset]);
    // console.log(tableConfig);
    const defaultColumn = React.useMemo(() => ({
        width: config.flexDefaultColumnGrow,
    }), [config.flexDefaultColumnGrow]);
    // Usando globalFilter di default effettua una ricerca su tutte le proprietà degli oggetti riportati in tabella.
    // Se volessi effettuare la ricerca solo su determinati campi dovrei creare una funzione filtro custom usando una libreria
    // tipo match-sorter (che però probabilmente ha il problema di riordinare i risultati), potrebbe essere più adatto fuse.js
    const additionalHooks = [useFilters, useGlobalFilter];
    if (config.sorting) {
        additionalHooks.push(useSortBy);
    }
    if (config.useExpanded) {
        additionalHooks.push(useExpanded);
    }
    if (config.paginate) {
        additionalHooks.push(usePagination);
    }
    if (config.selectable) {
        additionalHooks.push(useRowSelect);
    }
    if (config.useFlex) {
        additionalHooks.push(useFlexLayout);
        tableConfig.defaultColumn = defaultColumn;
    }
    const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow, pageCount, gotoPage, 
    // setFilter,
    setAllFilters, rows, setGlobalFilter, setPageSize, state, visibleColumns, } = useTable(tableConfig, ...additionalHooks, (hooks) => {
        if (config.useExpanded) {
            const Cell = ({ row }) => (React.createElement("span", Object.assign({}, row.getToggleRowExpandedProps()), row.isExpanded ? React.createElement(ChevronDown, null) : React.createElement(ChevronUp, null)));
            let additionalExpanderColumnProps = {};
            if (config.useFlex) {
                additionalExpanderColumnProps = {
                    style: Object.assign({ flex: '0 0 auto' }, config.expanderColumnStyle),
                };
            }
            hooks.visibleColumns.push((columns) => [
                Object.assign({ id: 'expander', Header: () => null, Cell }, additionalExpanderColumnProps),
                ...columns,
            ]);
        }
        if (config.selectable) {
            const Header = ({ getToggleAllRowsSelectedProps, getToggleAllPageRowsSelectedProps, }) => {
                var _a;
                const headerCheckboxProps = config.toggleAllSelectOnlyOnCurrentPage
                    ? getToggleAllPageRowsSelectedProps
                    : getToggleAllRowsSelectedProps;
                return (React.createElement(IndeterminateCheckbox, Object.assign({}, headerCheckboxProps(), { className: (_a = config.classNames) === null || _a === void 0 ? void 0 : _a.selectRowCheckboxClassName })));
            };
            const Cell = ({ row }) => {
                var _a;
                return (React.createElement(IndeterminateCheckbox, Object.assign({}, row.getToggleRowSelectedProps(), { className: (_a = config.classNames) === null || _a === void 0 ? void 0 : _a.selectRowCheckboxClassName })));
            };
            let additionalSelectColumnProps = {};
            if (config.useFlex) {
                additionalSelectColumnProps = {
                    style: Object.assign({ flex: '0 0 auto' }, config.selectColumnStyle),
                };
            }
            hooks.visibleColumns.push((columns) => [
                Object.assign({ id: 'selection', Header,
                    Cell }, additionalSelectColumnProps),
                ...columns,
            ]);
        }
    });
    if (config.debug) {
        /* eslint-disable-next-line */
        console.log(state);
    }
    const tableFilters = React.useMemo(() => {
        const filters = [];
        for (const filterKey in config.filters) {
            filters.push({
                id: filterKey,
                value: config.filters[filterKey],
            });
        }
        return filters;
    }, [config.filters]);
    useEffect(() => {
        // console.warn(tableFilters);
        // console.warn(appliedFilters);
        if (tableFilters) {
            setAllFilters(tableFilters);
            // tableFilters.forEach((f: any) => {
            //     // Applico i filtri solo se non sono gia applicati (ad esempio essendo i filtri di default)
            //     if (appliedFilters[f.id] !== f.value) {
            //         setFilter(f.id, f.value);
            //         setAppliedFilters({
            //             ...appliedFilters,
            //             [f.id]: f.value,
            //         });
            //     }
            // });
        }
    }, [tableFilters, setAllFilters]);
    useEffect(() => {
        if (config.globalFilter !== undefined) {
            setGlobalFilter(config.globalFilter);
        }
    }, [config.globalFilter, setGlobalFilter]);
    const rowsToDisplay = config.paginate ? page : rows;
    useEffect(() => {
        if (config.onChangeDisplayed) {
            config.onChangeDisplayed(rowsToDisplay.map((r) => r.original));
        }
        // eslint-disable-next-line
    }, [rowsToDisplay, config.onChangeDisplayed]);
    const TableTag = config.useFlex ? 'div' : 'table';
    const TheadTag = config.useFlex ? 'div' : 'thead';
    const TrTag = config.useFlex ? 'div' : 'tr';
    const ThTag = config.useFlex ? 'div' : 'th';
    const TbodyTag = config.useFlex ? 'div' : 'tbody';
    const TdTag = config.useFlex ? 'div' : 'td';
    const getCellFlexStyle = (horizontalAlign, verticalAlign = 'top') => {
        if (config.useFlex === false) {
            return {};
        }
        let justifyContent = null;
        let alignItems = null;
        switch (horizontalAlign) {
            case 'right':
                justifyContent = 'flex-end';
                break;
            case 'center':
                justifyContent = 'center';
                break;
            case 'left':
            default:
                justifyContent = 'flex-start';
        }
        switch (verticalAlign) {
            case 'top':
            default:
                alignItems = 'flex-start';
                break;
            case 'center':
                alignItems = 'center';
                break;
            case 'bottom':
                alignItems = 'flex-end';
                break;
        }
        return {
            display: 'flex',
            alignItems,
            justifyContent,
        };
    };
    // console.log(state);
    // console.log(rowsToDisplay);
    let additionalWidth = 0;
    if (config.useExpanded) {
        additionalWidth += ((_a = config.expanderColumnStyle) === null || _a === void 0 ? void 0 : _a.width) || 0;
    }
    if (config.selectable) {
        additionalWidth += ((_b = config.selectColumnStyle) === null || _b === void 0 ? void 0 : _b.width) || 0;
    }
    const additionalTableProps = {
        className: clsx('--tabulisk-table', (_c = config.classNames) === null || _c === void 0 ? void 0 : _c.tableClassName),
    };
    // Forzo la tabella ad occuppare tutto lo spazio possibile
    if (config.fullWidth) {
        additionalTableProps.style = {
            minWidth: '100%',
        };
    }
    else {
        const tableProps = getTableProps();
        if (((_d = tableProps === null || tableProps === void 0 ? void 0 : tableProps.style) === null || _d === void 0 ? void 0 : _d.minWidth) && config.useFlex) {
            // @ts-ignore
            const baseWidth = parseInt(tableProps.style.minWidth.replace('px', ''));
            if (additionalWidth > 0) {
                additionalTableProps.style = {
                    minWidth: `${baseWidth + additionalWidth}px`,
                };
            }
        }
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: clsx('--tabulisk-wrapper', (_e = config.classNames) === null || _e === void 0 ? void 0 : _e.tableWrapperClassName, {
                '--tabulisk-table-flex': config.useFlex,
            }) },
            React.createElement(TableTag, Object.assign({}, getTableProps(additionalTableProps)),
                React.createElement(TheadTag, { className: clsx('--tabulisk-thead', (_f = config.classNames) === null || _f === void 0 ? void 0 : _f.tableHeadClassName) }, headerGroups.map((headerGroup) => {
                    var _a, _b;
                    const headerProps = headerGroup.getHeaderGroupProps({
                        className: (_a = config.classNames) === null || _a === void 0 ? void 0 : _a.tableHeaderGroupClassName,
                    });
                    if (((_b = headerProps === null || headerProps === void 0 ? void 0 : headerProps.style) === null || _b === void 0 ? void 0 : _b.minWidth) && config.useFlex) {
                        const baseWidth = parseInt(headerProps.style.minWidth.replace('px', ''));
                        if (additionalWidth > 0) {
                            headerProps.style.minWidth = `${baseWidth + additionalWidth}px`;
                        }
                    }
                    return (React.createElement(TrTag, Object.assign({}, headerProps), headerGroup.headers.map((column) => {
                        // console.log(column);
                        var _a;
                        let headerProps = {
                            className: clsx((_a = config.classNames) === null || _a === void 0 ? void 0 : _a.tableHeaderClassName, column.className),
                            style: Object.assign(Object.assign({}, getCellFlexStyle(column.align, 'center')), column.style),
                        };
                        if (config.sorting) {
                            headerProps = Object.assign({}, headerProps);
                        }
                        const headerPropsSources = [headerProps];
                        if (config.sorting) {
                            headerPropsSources.push(column.getSortByToggleProps());
                        }
                        if (config.getColumnProps) {
                            headerPropsSources.push(config.getColumnProps(column));
                        }
                        if (config.getHeaderProps) {
                            headerPropsSources.push(config.getHeaderProps(column));
                        }
                        if (column.fixedWidth) {
                            headerPropsSources.push({
                                className: '',
                                style: {
                                    flex: '0 0 auto',
                                    width: column.fixedWidth,
                                },
                            });
                        }
                        if (typeof config.sortableColumnTitle !== 'undefined') {
                            headerPropsSources.push({
                                title: config.sortableColumnTitle,
                            });
                        }
                        /*console.log(column.getHeaderProps(headerPropsSources));

                        if (column.id === lastColumnId) {
                            const columProps: any = column.getHeaderProps(headerPropsSources);

                            columProps.style.width += scrollbarWidth();
                        }*/
                        // console.log(column.getHeaderProps(headerPropsSources));
                        return (React.createElement(ThTag, Object.assign({}, column.getHeaderProps(headerPropsSources)),
                            column.render('Header'),
                            column.isSorted ? (column.isSortedDesc ? (React.createElement(ChevronDown, null)) : (React.createElement(ChevronUp, null))) : (''),
                            column.canFilter && column.Filter
                                ? column.render('Filter', {
                                    reducer: config.useReducer || null,
                                })
                                : null));
                    })));
                })),
                React.createElement(TbodyTag, Object.assign({}, getTableBodyProps({
                    className: clsx('--tabulisk-tbody', (_g = config.classNames) === null || _g === void 0 ? void 0 : _g.tableBodyClassName, {
                        '--tabulisk-tbody-no-rows': rowsToDisplay.length === 0,
                    }),
                })),
                    rowsToDisplay.length === 0 && (React.createElement(TrTag, null,
                        React.createElement(TdTag, { className: clsx('--tabulisk-no-rows-container', (_h = config.classNames) === null || _h === void 0 ? void 0 : _h.noRowsContainerClassName) }, config.noRowsMessage))),
                    rowsToDisplay.map((row) => {
                        var _a, _b, _c, _d;
                        prepareRow(row);
                        const rowProps = row.getRowProps([
                            {
                                className: clsx((_a = config.classNames) === null || _a === void 0 ? void 0 : _a.tableRowClassName, row.isSelected && '--tabulisk-row-selected'),
                            },
                            config.getRowProps ? config.getRowProps(row) : {},
                        ]);
                        if (((_b = rowProps === null || rowProps === void 0 ? void 0 : rowProps.style) === null || _b === void 0 ? void 0 : _b.minWidth) && config.useFlex) {
                            const baseWidth = parseInt(rowProps.style.minWidth.replace('px', ''));
                            if (additionalWidth > 0) {
                                rowProps.style.minWidth = `${baseWidth + additionalWidth}px`;
                            }
                        }
                        const key = rowProps.key;
                        delete rowProps.key;
                        return (React.createElement(React.Fragment, { key: key },
                            React.createElement(TrTag, Object.assign({}, rowProps), row.cells.map((cell) => {
                                var _a;
                                return (React.createElement(TdTag, Object.assign({}, cell.getCellProps([
                                    {
                                        className: clsx((_a = config.classNames) === null || _a === void 0 ? void 0 : _a.tableRowCellClassName, cell.column.className),
                                        style: Object.assign(Object.assign({}, getCellFlexStyle(cell.column.align, cell.column.vAlign)), cell.column.style),
                                    },
                                    config.getColumnProps
                                        ? config.getColumnProps(cell.column)
                                        : {},
                                    config.getCellProps
                                        ? config.getCellProps(cell)
                                        : {},
                                    cell.column.fixedWidth
                                        ? {
                                            style: {
                                                flex: '0 0 auto',
                                                width: cell.column.fixedWidth,
                                            },
                                        }
                                        : {},
                                ])), cell.render('Cell', {
                                    reducer: config.useReducer || null,
                                })));
                            })),
                            row.isExpanded ? (React.createElement(TrTag, { className: (_c = config.classNames) === null || _c === void 0 ? void 0 : _c.rowSubWrapperClassName },
                                React.createElement(TdTag, { colSpan: visibleColumns.length, className: (_d = config.classNames) === null || _d === void 0 ? void 0 : _d.rowSubComponentClassName }, config.renderRowSubComponent
                                    ? config.renderRowSubComponent({ row })
                                    : null))) : null));
                    })))),
        config.paginate && rowsToDisplay.length > 0 && (React.createElement(Paginator, { showPageSizeSelector: config.showPageSizeSelector, pageCount: pageCount, pageSize: state.pageSize, pageIndex: state.pageIndex, total: tableConfig.manualPagination ? config.total || 0 : rows.length, gotoPage: gotoPage, onChangePageSize: (pageSize) => setPageSize(parseInt(pageSize)), PaginatorInfoComponent: config.PaginatorInfoComponent, pageSizeOptions: config.paginatorPageSizeOptions, selectorPageLabel: config.paginatorSelectorPageLabel, 
            // minimal={true}
            classNames: {
                paginatorWrapperClassName: (_j = config.classNames) === null || _j === void 0 ? void 0 : _j.paginatorWrapperClassName,
                paginatorClassName: (_k = config.classNames) === null || _k === void 0 ? void 0 : _k.paginatorClassName,
                paginatorItemClassName: (_l = config.classNames) === null || _l === void 0 ? void 0 : _l.paginatorItemClassName,
                paginatorPageSizeSelectorClassName: (_m = config.classNames) === null || _m === void 0 ? void 0 : _m.paginatorPageSizeSelectorClassName,
                paginatorInfoClassName: (_o = config.classNames) === null || _o === void 0 ? void 0 : _o.paginatorInfoClassName,
            } }))));
}
const DataTable = React.memo(DataTableComponent);

function n(n){for(var r=arguments.length,t=Array(r>1?r-1:0),e=1;e<r;e++)t[e-1]=arguments[e];if("production"!==process.env.NODE_ENV){var i=Y[n],o=i?"function"==typeof i?i.apply(null,t):i:"unknown error nr: "+n;throw Error("[Immer] "+o)}throw Error("[Immer] minified error nr: "+n+(t.length?" "+t.map((function(n){return "'"+n+"'"})).join(","):"")+". Find the full error at: https://bit.ly/3cXEKWf")}function r(n){return !!n&&!!n[Q]}function t(n){return !!n&&(function(n){if(!n||"object"!=typeof n)return !1;var r=Object.getPrototypeOf(n);if(null===r)return !0;var t=Object.hasOwnProperty.call(r,"constructor")&&r.constructor;return "function"==typeof t&&Function.toString.call(t)===Z}(n)||Array.isArray(n)||!!n[L]||!!n.constructor[L]||s(n)||v(n))}function i(n,r,t){void 0===t&&(t=!1),0===o(n)?(t?Object.keys:nn)(n).forEach((function(e){t&&"symbol"==typeof e||r(e,n[e],n);})):n.forEach((function(t,e){return r(e,t,n)}));}function o(n){var r=n[Q];return r?r.i>3?r.i-4:r.i:Array.isArray(n)?1:s(n)?2:v(n)?3:0}function u(n,r){return 2===o(n)?n.has(r):Object.prototype.hasOwnProperty.call(n,r)}function a(n,r){return 2===o(n)?n.get(r):n[r]}function f(n,r,t){var e=o(n);2===e?n.set(r,t):3===e?(n.delete(r),n.add(t)):n[r]=t;}function c(n,r){return n===r?0!==n||1/n==1/r:n!=n&&r!=r}function s(n){return X&&n instanceof Map}function v(n){return q&&n instanceof Set}function p(n){return n.o||n.t}function l(n){if(Array.isArray(n))return Array.prototype.slice.call(n);var r=rn(n);delete r[Q];for(var t=nn(r),e=0;e<t.length;e++){var i=t[e],o=r[i];!1===o.writable&&(o.writable=!0,o.configurable=!0),(o.get||o.set)&&(r[i]={configurable:!0,writable:!0,enumerable:o.enumerable,value:n[i]});}return Object.create(Object.getPrototypeOf(n),r)}function d(n,e){return void 0===e&&(e=!1),y(n)||r(n)||!t(n)?n:(o(n)>1&&(n.set=n.add=n.clear=n.delete=h),Object.freeze(n),e&&i(n,(function(n,r){return d(r,!0)}),!0),n)}function h(){n(2);}function y(n){return null==n||"object"!=typeof n||Object.isFrozen(n)}function b(r){var t=tn[r];return t||n(18,r),t}function _(){return "production"===process.env.NODE_ENV||U||n(0),U}function j(n,r){r&&(b("Patches"),n.u=[],n.s=[],n.v=r);}function g(n){O(n),n.p.forEach(S),n.p=null;}function O(n){n===U&&(U=n.l);}function w(n){return U={p:[],l:U,h:n,m:!0,_:0}}function S(n){var r=n[Q];0===r.i||1===r.i?r.j():r.g=!0;}function P(r,e){e._=e.p.length;var i=e.p[0],o=void 0!==r&&r!==i;return e.h.O||b("ES5").S(e,r,o),o?(i[Q].P&&(g(e),n(4)),t(r)&&(r=M(e,r),e.l||x(e,r)),e.u&&b("Patches").M(i[Q],r,e.u,e.s)):r=M(e,i,[]),g(e),e.u&&e.v(e.u,e.s),r!==H?r:void 0}function M(n,r,t){if(y(r))return r;var e=r[Q];if(!e)return i(r,(function(i,o){return A(n,e,r,i,o,t)}),!0),r;if(e.A!==n)return r;if(!e.P)return x(n,e.t,!0),e.t;if(!e.I){e.I=!0,e.A._--;var o=4===e.i||5===e.i?e.o=l(e.k):e.o;i(3===e.i?new Set(o):o,(function(r,i){return A(n,e,o,r,i,t)})),x(n,o,!1),t&&n.u&&b("Patches").R(e,t,n.u,n.s);}return e.o}function A(e,i,o,a,c,s){if("production"!==process.env.NODE_ENV&&c===o&&n(5),r(c)){var v=M(e,c,s&&i&&3!==i.i&&!u(i.D,a)?s.concat(a):void 0);if(f(o,a,v),!r(v))return;e.m=!1;}if(t(c)&&!y(c)){if(!e.h.F&&e._<1)return;M(e,c),i&&i.A.l||x(e,c);}}function x(n,r,t){void 0===t&&(t=!1),n.h.F&&n.m&&d(r,t);}function z(n,r){var t=n[Q];return (t?p(t):n)[r]}function I(n,r){if(r in n)for(var t=Object.getPrototypeOf(n);t;){var e=Object.getOwnPropertyDescriptor(t,r);if(e)return e;t=Object.getPrototypeOf(t);}}function k(n){n.P||(n.P=!0,n.l&&k(n.l));}function E(n){n.o||(n.o=l(n.t));}function R(n,r,t){var e=s(r)?b("MapSet").N(r,t):v(r)?b("MapSet").T(r,t):n.O?function(n,r){var t=Array.isArray(n),e={i:t?1:0,A:r?r.A:_(),P:!1,I:!1,D:{},l:r,t:n,k:null,o:null,j:null,C:!1},i=e,o=en;t&&(i=[e],o=on);var u=Proxy.revocable(i,o),a=u.revoke,f=u.proxy;return e.k=f,e.j=a,f}(r,t):b("ES5").J(r,t);return (t?t.A:_()).p.push(e),e}function D(e){return r(e)||n(22,e),function n(r){if(!t(r))return r;var e,u=r[Q],c=o(r);if(u){if(!u.P&&(u.i<4||!b("ES5").K(u)))return u.t;u.I=!0,e=F(r,c),u.I=!1;}else e=F(r,c);return i(e,(function(r,t){u&&a(u.t,r)===t||f(e,r,n(t));})),3===c?new Set(e):e}(e)}function F(n,r){switch(r){case 2:return new Map(n);case 3:return Array.from(n)}return l(n)}var G,U,W="undefined"!=typeof Symbol&&"symbol"==typeof Symbol("x"),X="undefined"!=typeof Map,q="undefined"!=typeof Set,B="undefined"!=typeof Proxy&&void 0!==Proxy.revocable&&"undefined"!=typeof Reflect,H=W?Symbol.for("immer-nothing"):((G={})["immer-nothing"]=!0,G),L=W?Symbol.for("immer-draftable"):"__$immer_draftable",Q=W?Symbol.for("immer-state"):"__$immer_state",Y={0:"Illegal state",1:"Immer drafts cannot have computed properties",2:"This object has been frozen and should not be mutated",3:function(n){return "Cannot use a proxy that has been revoked. Did you pass an object from inside an immer function to an async process? "+n},4:"An immer producer returned a new value *and* modified its draft. Either return a new value *or* modify the draft.",5:"Immer forbids circular references",6:"The first or second argument to `produce` must be a function",7:"The third argument to `produce` must be a function or undefined",8:"First argument to `createDraft` must be a plain object, an array, or an immerable object",9:"First argument to `finishDraft` must be a draft returned by `createDraft`",10:"The given draft is already finalized",11:"Object.defineProperty() cannot be used on an Immer draft",12:"Object.setPrototypeOf() cannot be used on an Immer draft",13:"Immer only supports deleting array indices",14:"Immer only supports setting array indices and the 'length' property",15:function(n){return "Cannot apply patch, path doesn't resolve: "+n},16:'Sets cannot have "replace" patches.',17:function(n){return "Unsupported patch operation: "+n},18:function(n){return "The plugin for '"+n+"' has not been loaded into Immer. To enable the plugin, import and call `enable"+n+"()` when initializing your application."},20:"Cannot use proxies if Proxy, Proxy.revocable or Reflect are not available",21:function(n){return "produce can only be called on things that are draftable: plain objects, arrays, Map, Set or classes that are marked with '[immerable]: true'. Got '"+n+"'"},22:function(n){return "'current' expects a draft, got: "+n},23:function(n){return "'original' expects a draft, got: "+n},24:"Patching reserved attributes like __proto__, prototype and constructor is not allowed"},Z=""+Object.prototype.constructor,nn="undefined"!=typeof Reflect&&Reflect.ownKeys?Reflect.ownKeys:void 0!==Object.getOwnPropertySymbols?function(n){return Object.getOwnPropertyNames(n).concat(Object.getOwnPropertySymbols(n))}:Object.getOwnPropertyNames,rn=Object.getOwnPropertyDescriptors||function(n){var r={};return nn(n).forEach((function(t){r[t]=Object.getOwnPropertyDescriptor(n,t);})),r},tn={},en={get:function(n,r){if(r===Q)return n;var e=p(n);if(!u(e,r))return function(n,r,t){var e,i=I(r,t);return i?"value"in i?i.value:null===(e=i.get)||void 0===e?void 0:e.call(n.k):void 0}(n,e,r);var i=e[r];return n.I||!t(i)?i:i===z(n.t,r)?(E(n),n.o[r]=R(n.A.h,i,n)):i},has:function(n,r){return r in p(n)},ownKeys:function(n){return Reflect.ownKeys(p(n))},set:function(n,r,t){var e=I(p(n),r);if(null==e?void 0:e.set)return e.set.call(n.k,t),!0;if(!n.P){var i=z(p(n),r),o=null==i?void 0:i[Q];if(o&&o.t===t)return n.o[r]=t,n.D[r]=!1,!0;if(c(t,i)&&(void 0!==t||u(n.t,r)))return !0;E(n),k(n);}return n.o[r]===t&&"number"!=typeof t||(n.o[r]=t,n.D[r]=!0,!0)},deleteProperty:function(n,r){return void 0!==z(n.t,r)||r in n.t?(n.D[r]=!1,E(n),k(n)):delete n.D[r],n.o&&delete n.o[r],!0},getOwnPropertyDescriptor:function(n,r){var t=p(n),e=Reflect.getOwnPropertyDescriptor(t,r);return e?{writable:!0,configurable:1!==n.i||"length"!==r,enumerable:e.enumerable,value:t[r]}:e},defineProperty:function(){n(11);},getPrototypeOf:function(n){return Object.getPrototypeOf(n.t)},setPrototypeOf:function(){n(12);}},on={};i(en,(function(n,r){on[n]=function(){return arguments[0]=arguments[0][0],r.apply(this,arguments)};})),on.deleteProperty=function(r,t){return "production"!==process.env.NODE_ENV&&isNaN(parseInt(t))&&n(13),en.deleteProperty.call(this,r[0],t)},on.set=function(r,t,e){return "production"!==process.env.NODE_ENV&&"length"!==t&&isNaN(parseInt(t))&&n(14),en.set.call(this,r[0],t,e,r[0])};var un=function(){function e(r){var e=this;this.O=B,this.F=!0,this.produce=function(r,i,o){if("function"==typeof r&&"function"!=typeof i){var u=i;i=r;var a=e;return function(n){var r=this;void 0===n&&(n=u);for(var t=arguments.length,e=Array(t>1?t-1:0),o=1;o<t;o++)e[o-1]=arguments[o];return a.produce(n,(function(n){var t;return (t=i).call.apply(t,[r,n].concat(e))}))}}var f;if("function"!=typeof i&&n(6),void 0!==o&&"function"!=typeof o&&n(7),t(r)){var c=w(e),s=R(e,r,void 0),v=!0;try{f=i(s),v=!1;}finally{v?g(c):O(c);}return "undefined"!=typeof Promise&&f instanceof Promise?f.then((function(n){return j(c,o),P(n,c)}),(function(n){throw g(c),n})):(j(c,o),P(f,c))}if(!r||"object"!=typeof r){if((f=i(r))===H)return;return void 0===f&&(f=r),e.F&&d(f,!0),f}n(21,r);},this.produceWithPatches=function(n,r){return "function"==typeof n?function(r){for(var t=arguments.length,i=Array(t>1?t-1:0),o=1;o<t;o++)i[o-1]=arguments[o];return e.produceWithPatches(r,(function(r){return n.apply(void 0,[r].concat(i))}))}:[e.produce(n,r,(function(n,r){t=n,i=r;})),t,i];var t,i;},"boolean"==typeof(null==r?void 0:r.useProxies)&&this.setUseProxies(r.useProxies),"boolean"==typeof(null==r?void 0:r.autoFreeze)&&this.setAutoFreeze(r.autoFreeze);}var i=e.prototype;return i.createDraft=function(e){t(e)||n(8),r(e)&&(e=D(e));var i=w(this),o=R(this,e,void 0);return o[Q].C=!0,O(i),o},i.finishDraft=function(r,t){var e=r&&r[Q];"production"!==process.env.NODE_ENV&&(e&&e.C||n(9),e.I&&n(10));var i=e.A;return j(i,t),P(void 0,i)},i.setAutoFreeze=function(n){this.F=n;},i.setUseProxies=function(r){r&&!B&&n(20),this.O=r;},i.applyPatches=function(n,t){var e;for(e=t.length-1;e>=0;e--){var i=t[e];if(0===i.path.length&&"replace"===i.op){n=i.value;break}}var o=b("Patches").$;return r(n)?o(n,t):this.produce(n,(function(n){return o(n,t.slice(e+1))}))},e}(),an=new un,fn=an.produce;an.produceWithPatches.bind(an);an.setAutoFreeze.bind(an);an.setUseProxies.bind(an);an.applyPatches.bind(an);an.createDraft.bind(an);an.finishDraft.bind(an);

function createCommonjsModule(fn) {
  var module = { exports: {} };
	return fn(module, module.exports), module.exports;
}

var strictUriEncode = str => encodeURIComponent(str).replace(/[!'()*]/g, x => `%${x.charCodeAt(0).toString(16).toUpperCase()}`);

var token = '%[a-f0-9]{2}';
var singleMatcher = new RegExp(token, 'gi');
var multiMatcher = new RegExp('(' + token + ')+', 'gi');

function decodeComponents(components, split) {
	try {
		// Try to decode the entire string first
		return decodeURIComponent(components.join(''));
	} catch (err) {
		// Do nothing
	}

	if (components.length === 1) {
		return components;
	}

	split = split || 1;

	// Split the array in 2 parts
	var left = components.slice(0, split);
	var right = components.slice(split);

	return Array.prototype.concat.call([], decodeComponents(left), decodeComponents(right));
}

function decode(input) {
	try {
		return decodeURIComponent(input);
	} catch (err) {
		var tokens = input.match(singleMatcher);

		for (var i = 1; i < tokens.length; i++) {
			input = decodeComponents(tokens, i).join('');

			tokens = input.match(singleMatcher);
		}

		return input;
	}
}

function customDecodeURIComponent(input) {
	// Keep track of all the replacements and prefill the map with the `BOM`
	var replaceMap = {
		'%FE%FF': '\uFFFD\uFFFD',
		'%FF%FE': '\uFFFD\uFFFD'
	};

	var match = multiMatcher.exec(input);
	while (match) {
		try {
			// Decode as big chunks as possible
			replaceMap[match[0]] = decodeURIComponent(match[0]);
		} catch (err) {
			var result = decode(match[0]);

			if (result !== match[0]) {
				replaceMap[match[0]] = result;
			}
		}

		match = multiMatcher.exec(input);
	}

	// Add `%C2` at the end of the map to make sure it does not replace the combinator before everything else
	replaceMap['%C2'] = '\uFFFD';

	var entries = Object.keys(replaceMap);

	for (var i = 0; i < entries.length; i++) {
		// Replace all decoded components
		var key = entries[i];
		input = input.replace(new RegExp(key, 'g'), replaceMap[key]);
	}

	return input;
}

var decodeUriComponent = function (encodedURI) {
	if (typeof encodedURI !== 'string') {
		throw new TypeError('Expected `encodedURI` to be of type `string`, got `' + typeof encodedURI + '`');
	}

	try {
		encodedURI = encodedURI.replace(/\+/g, ' ');

		// Try the built in decoder first
		return decodeURIComponent(encodedURI);
	} catch (err) {
		// Fallback to a more advanced decoder
		return customDecodeURIComponent(encodedURI);
	}
};

var splitOnFirst = (string, separator) => {
	if (!(typeof string === 'string' && typeof separator === 'string')) {
		throw new TypeError('Expected the arguments to be of type `string`');
	}

	if (separator === '') {
		return [string];
	}

	const separatorIndex = string.indexOf(separator);

	if (separatorIndex === -1) {
		return [string];
	}

	return [
		string.slice(0, separatorIndex),
		string.slice(separatorIndex + separator.length)
	];
};

var filterObj = function (obj, predicate) {
	var ret = {};
	var keys = Object.keys(obj);
	var isArr = Array.isArray(predicate);

	for (var i = 0; i < keys.length; i++) {
		var key = keys[i];
		var val = obj[key];

		if (isArr ? predicate.indexOf(key) !== -1 : predicate(key, val, obj)) {
			ret[key] = val;
		}
	}

	return ret;
};

var queryString = createCommonjsModule(function (module, exports) {





const isNullOrUndefined = value => value === null || value === undefined;

function encoderForArrayFormat(options) {
	switch (options.arrayFormat) {
		case 'index':
			return key => (result, value) => {
				const index = result.length;

				if (
					value === undefined ||
					(options.skipNull && value === null) ||
					(options.skipEmptyString && value === '')
				) {
					return result;
				}

				if (value === null) {
					return [...result, [encode(key, options), '[', index, ']'].join('')];
				}

				return [
					...result,
					[encode(key, options), '[', encode(index, options), ']=', encode(value, options)].join('')
				];
			};

		case 'bracket':
			return key => (result, value) => {
				if (
					value === undefined ||
					(options.skipNull && value === null) ||
					(options.skipEmptyString && value === '')
				) {
					return result;
				}

				if (value === null) {
					return [...result, [encode(key, options), '[]'].join('')];
				}

				return [...result, [encode(key, options), '[]=', encode(value, options)].join('')];
			};

		case 'comma':
		case 'separator':
		case 'bracket-separator': {
			const keyValueSep = options.arrayFormat === 'bracket-separator' ?
				'[]=' :
				'=';

			return key => (result, value) => {
				if (
					value === undefined ||
					(options.skipNull && value === null) ||
					(options.skipEmptyString && value === '')
				) {
					return result;
				}

				// Translate null to an empty string so that it doesn't serialize as 'null'
				value = value === null ? '' : value;

				if (result.length === 0) {
					return [[encode(key, options), keyValueSep, encode(value, options)].join('')];
				}

				return [[result, encode(value, options)].join(options.arrayFormatSeparator)];
			};
		}

		default:
			return key => (result, value) => {
				if (
					value === undefined ||
					(options.skipNull && value === null) ||
					(options.skipEmptyString && value === '')
				) {
					return result;
				}

				if (value === null) {
					return [...result, encode(key, options)];
				}

				return [...result, [encode(key, options), '=', encode(value, options)].join('')];
			};
	}
}

function parserForArrayFormat(options) {
	let result;

	switch (options.arrayFormat) {
		case 'index':
			return (key, value, accumulator) => {
				result = /\[(\d*)\]$/.exec(key);

				key = key.replace(/\[\d*\]$/, '');

				if (!result) {
					accumulator[key] = value;
					return;
				}

				if (accumulator[key] === undefined) {
					accumulator[key] = {};
				}

				accumulator[key][result[1]] = value;
			};

		case 'bracket':
			return (key, value, accumulator) => {
				result = /(\[\])$/.exec(key);
				key = key.replace(/\[\]$/, '');

				if (!result) {
					accumulator[key] = value;
					return;
				}

				if (accumulator[key] === undefined) {
					accumulator[key] = [value];
					return;
				}

				accumulator[key] = [].concat(accumulator[key], value);
			};

		case 'comma':
		case 'separator':
			return (key, value, accumulator) => {
				const isArray = typeof value === 'string' && value.includes(options.arrayFormatSeparator);
				const isEncodedArray = (typeof value === 'string' && !isArray && decode(value, options).includes(options.arrayFormatSeparator));
				value = isEncodedArray ? decode(value, options) : value;
				const newValue = isArray || isEncodedArray ? value.split(options.arrayFormatSeparator).map(item => decode(item, options)) : value === null ? value : decode(value, options);
				accumulator[key] = newValue;
			};

		case 'bracket-separator':
			return (key, value, accumulator) => {
				const isArray = /(\[\])$/.test(key);
				key = key.replace(/\[\]$/, '');

				if (!isArray) {
					accumulator[key] = value ? decode(value, options) : value;
					return;
				}

				const arrayValue = value === null ?
					[] :
					value.split(options.arrayFormatSeparator).map(item => decode(item, options));

				if (accumulator[key] === undefined) {
					accumulator[key] = arrayValue;
					return;
				}

				accumulator[key] = [].concat(accumulator[key], arrayValue);
			};

		default:
			return (key, value, accumulator) => {
				if (accumulator[key] === undefined) {
					accumulator[key] = value;
					return;
				}

				accumulator[key] = [].concat(accumulator[key], value);
			};
	}
}

function validateArrayFormatSeparator(value) {
	if (typeof value !== 'string' || value.length !== 1) {
		throw new TypeError('arrayFormatSeparator must be single character string');
	}
}

function encode(value, options) {
	if (options.encode) {
		return options.strict ? strictUriEncode(value) : encodeURIComponent(value);
	}

	return value;
}

function decode(value, options) {
	if (options.decode) {
		return decodeUriComponent(value);
	}

	return value;
}

function keysSorter(input) {
	if (Array.isArray(input)) {
		return input.sort();
	}

	if (typeof input === 'object') {
		return keysSorter(Object.keys(input))
			.sort((a, b) => Number(a) - Number(b))
			.map(key => input[key]);
	}

	return input;
}

function removeHash(input) {
	const hashStart = input.indexOf('#');
	if (hashStart !== -1) {
		input = input.slice(0, hashStart);
	}

	return input;
}

function getHash(url) {
	let hash = '';
	const hashStart = url.indexOf('#');
	if (hashStart !== -1) {
		hash = url.slice(hashStart);
	}

	return hash;
}

function extract(input) {
	input = removeHash(input);
	const queryStart = input.indexOf('?');
	if (queryStart === -1) {
		return '';
	}

	return input.slice(queryStart + 1);
}

function parseValue(value, options) {
	if (options.parseNumbers && !Number.isNaN(Number(value)) && (typeof value === 'string' && value.trim() !== '')) {
		value = Number(value);
	} else if (options.parseBooleans && value !== null && (value.toLowerCase() === 'true' || value.toLowerCase() === 'false')) {
		value = value.toLowerCase() === 'true';
	}

	return value;
}

function parse(query, options) {
	options = Object.assign({
		decode: true,
		sort: true,
		arrayFormat: 'none',
		arrayFormatSeparator: ',',
		parseNumbers: false,
		parseBooleans: false
	}, options);

	validateArrayFormatSeparator(options.arrayFormatSeparator);

	const formatter = parserForArrayFormat(options);

	// Create an object with no prototype
	const ret = Object.create(null);

	if (typeof query !== 'string') {
		return ret;
	}

	query = query.trim().replace(/^[?#&]/, '');

	if (!query) {
		return ret;
	}

	for (const param of query.split('&')) {
		if (param === '') {
			continue;
		}

		let [key, value] = splitOnFirst(options.decode ? param.replace(/\+/g, ' ') : param, '=');

		// Missing `=` should be `null`:
		// http://w3.org/TR/2012/WD-url-20120524/#collect-url-parameters
		value = value === undefined ? null : ['comma', 'separator', 'bracket-separator'].includes(options.arrayFormat) ? value : decode(value, options);
		formatter(decode(key, options), value, ret);
	}

	for (const key of Object.keys(ret)) {
		const value = ret[key];
		if (typeof value === 'object' && value !== null) {
			for (const k of Object.keys(value)) {
				value[k] = parseValue(value[k], options);
			}
		} else {
			ret[key] = parseValue(value, options);
		}
	}

	if (options.sort === false) {
		return ret;
	}

	return (options.sort === true ? Object.keys(ret).sort() : Object.keys(ret).sort(options.sort)).reduce((result, key) => {
		const value = ret[key];
		if (Boolean(value) && typeof value === 'object' && !Array.isArray(value)) {
			// Sort object keys, not values
			result[key] = keysSorter(value);
		} else {
			result[key] = value;
		}

		return result;
	}, Object.create(null));
}

exports.extract = extract;
exports.parse = parse;

exports.stringify = (object, options) => {
	if (!object) {
		return '';
	}

	options = Object.assign({
		encode: true,
		strict: true,
		arrayFormat: 'none',
		arrayFormatSeparator: ','
	}, options);

	validateArrayFormatSeparator(options.arrayFormatSeparator);

	const shouldFilter = key => (
		(options.skipNull && isNullOrUndefined(object[key])) ||
		(options.skipEmptyString && object[key] === '')
	);

	const formatter = encoderForArrayFormat(options);

	const objectCopy = {};

	for (const key of Object.keys(object)) {
		if (!shouldFilter(key)) {
			objectCopy[key] = object[key];
		}
	}

	const keys = Object.keys(objectCopy);

	if (options.sort !== false) {
		keys.sort(options.sort);
	}

	return keys.map(key => {
		const value = object[key];

		if (value === undefined) {
			return '';
		}

		if (value === null) {
			return encode(key, options);
		}

		if (Array.isArray(value)) {
			if (value.length === 0 && options.arrayFormat === 'bracket-separator') {
				return encode(key, options) + '[]';
			}

			return value
				.reduce(formatter(key), [])
				.join('&');
		}

		return encode(key, options) + '=' + encode(value, options);
	}).filter(x => x.length > 0).join('&');
};

exports.parseUrl = (url, options) => {
	options = Object.assign({
		decode: true
	}, options);

	const [url_, hash] = splitOnFirst(url, '#');

	return Object.assign(
		{
			url: url_.split('?')[0] || '',
			query: parse(extract(url), options)
		},
		options && options.parseFragmentIdentifier && hash ? {fragmentIdentifier: decode(hash, options)} : {}
	);
};

exports.stringifyUrl = (object, options) => {
	options = Object.assign({
		encode: true,
		strict: true
	}, options);

	const url = removeHash(object.url).split('?')[0] || '';
	const queryFromUrl = exports.extract(object.url);
	const parsedQueryFromUrl = exports.parse(queryFromUrl, {sort: false});

	const query = Object.assign(parsedQueryFromUrl, object.query);
	let queryString = exports.stringify(query, options);
	if (queryString) {
		queryString = `?${queryString}`;
	}

	let hash = getHash(object.url);
	if (object.fragmentIdentifier) {
		hash = `#${encode(object.fragmentIdentifier, options)}`;
	}

	return `${url}${queryString}${hash}`;
};

exports.pick = (input, filter, options) => {
	options = Object.assign({
		parseFragmentIdentifier: true
	}, options);

	const {url, query, fragmentIdentifier} = exports.parseUrl(input, options);
	return exports.stringifyUrl({
		url,
		query: filterObj(query, filter),
		fragmentIdentifier
	}, options);
};

exports.exclude = (input, filter, options) => {
	const exclusionFilter = Array.isArray(filter) ? key => !filter.includes(key) : (key, value) => !filter(key, value);

	return exports.pick(input, exclusionFilter, options);
};
});

const SET_PAGE = 'SET_PAGE';
const SET_PAGE_SIZE = 'SET_PAGE_SIZE';
const SET_GLOBAL_FILTER = 'SET_GLOBAL_FILTER';
const SET_FILTER = 'SET_FILTER';
const TOGGLE_SORT_BY = 'TOGGLE_SORT_BY';
const RESET_FILTER = 'RESET_FILTER';
const RESET_FILTERS = 'RESET_FILTERS';
const REPLACE_STATE = 'REPLACE_STATE';
const DEFAULT_STATE = {
    filters: {},
    sortBy: [],
    globalFilter: '',
    page: 1,
    pageSize: 20,
    autoResetPage: true,
};
function initTableFilterState(state) {
    return defaults(state, DEFAULT_STATE);
}
const tableFilterReducer = fn((draft, action) => {
    const { type, payload } = action;
    switch (type) {
        case SET_PAGE:
            draft.page = payload + 1; // +1 dato che pageIndex è base 0
            break;
        case SET_PAGE_SIZE:
            draft.pageSize = payload;
            if (draft.autoResetPage) {
                draft.page = 1;
            }
            break;
        case SET_GLOBAL_FILTER:
            draft.globalFilter = payload;
            if (draft.autoResetPage) {
                draft.page = 1;
            }
            break;
        case SET_FILTER:
            draft.filters[payload.key] = payload.value;
            if (draft.autoResetPage) {
                draft.page = 1;
            }
            break;
        case TOGGLE_SORT_BY:
            // draft.sortBy = [`${payload.key}::asc`];
            const { key, multi } = payload;
            if (multi === false) {
                const already = draft.sortBy.find((sortItem) => sortItem.id === key);
                if (!already) {
                    draft.sortBy = [
                        {
                            id: key,
                            desc: false,
                        },
                    ];
                }
                else {
                    const currentDir = draft.sortBy[0].desc ? 'desc' : 'asc';
                    if (currentDir === 'desc') {
                        draft.sortBy = [];
                    }
                    else {
                        draft.sortBy = [
                            {
                                id: key,
                                desc: true,
                            },
                        ];
                    }
                }
            }
            else {
                const index = draft.sortBy.findIndex((sortItem) => sortItem.id === key);
                if (index === -1) {
                    draft.sortBy.push({
                        id: key,
                        desc: false,
                    });
                }
                else {
                    const currentDir = draft.sortBy[index].desc ? 'desc' : 'asc';
                    if (currentDir === 'desc') {
                        draft.sortBy.splice(index, 1);
                    }
                    else {
                        draft.sortBy[index].desc = true;
                    }
                }
            }
            break;
        case RESET_FILTER:
            draft.filters[payload] = null;
            break;
        case RESET_FILTERS:
            return initTableFilterState(payload);
        case REPLACE_STATE:
            const updated = Object.assign(Object.assign({}, draft), payload);
            updated.filters = payload.filters || {};
            return updated;
        default:
            throw new Error('Unknown action');
    }
});
function bindActions(dispatch, overrides) {
    return {
        setPage: (page) => {
            return dispatch({
                type: SET_PAGE,
                payload: page,
            });
        },
        setPageSize: (pageSize) => {
            return dispatch({
                type: SET_PAGE_SIZE,
                payload: pageSize,
            });
        },
        setFilter: (key, value) => {
            return dispatch({
                type: SET_FILTER,
                payload: {
                    key,
                    value,
                },
            });
        },
        setGlobalFilter: (term) => {
            return dispatch({
                type: SET_GLOBAL_FILTER,
                payload: term,
            });
        },
        toggleSortBy: (key, multi = false) => {
            return dispatch({
                type: TOGGLE_SORT_BY,
                payload: {
                    key,
                    multi,
                },
            });
        },
        resetFilter: (key) => {
            return dispatch({
                type: RESET_FILTER,
                payload: key,
            });
        },
        resetFilters: (state) => {
            return dispatch({
                type: RESET_FILTERS,
                payload: state || { pageSize: overrides === null || overrides === void 0 ? void 0 : overrides.pageSize },
            });
        },
        replaceState: (state) => {
            return dispatch({
                type: REPLACE_STATE,
                payload: state,
            });
        },
        resetToInitialState: () => {
            return dispatch({
                type: RESET_FILTERS,
                payload: overrides || {},
            });
        },
    };
}
function useDataTableState(overrides) {
    const [state, dispatch] = useReducer(tableFilterReducer, overrides, initTableFilterState);
    const actions = useMemo(() => bindActions(dispatch, overrides), [overrides]);
    // const remoteDataTableProps = getRemoteDataTableProps(state, actions);
    return { state, actions, dispatch };
}
// Funzione generica per calcolare le differenze tra due oggetti
function difference(object, base) {
    function changes(object, base) {
        return transform(object, (result, value, key) => {
            if (!isEqual(value, base[key])) {
                result[key] =
                    isObject(value) && isObject(base[key]) ? changes(value, base[key]) : value;
            }
        });
    }
    return changes(object, base);
}
// Chiavi dello stato che non sono filtri, necessario saperlo per calcolare la querystring
const nonFilterKeys = ['sortBy', 'globalFilter', 'page', 'pageSize'];
const getStateFromQs = (search) => {
    const queryString$1 = queryString.parse(search);
    const queryStringState = transform(queryString$1, (obj, value, key) => {
        if (includes(nonFilterKeys, key)) {
            obj[key] = key === 'pageSize' ? parseInt(value) : value;
            if (key === 'sortBy') {
                if (typeof value === 'string') {
                    const [id, dir] = value.split('::');
                    obj[key] = [
                        {
                            id,
                            desc: dir === 'desc',
                        },
                    ];
                }
                else {
                    obj[key] = value.map((sortItem) => {
                        const [id, dir] = sortItem.split('::');
                        return {
                            id,
                            desc: dir === 'desc',
                        };
                    });
                }
            }
        }
        else {
            if (!obj.filters) {
                obj.filters = {};
            }
            if (value === 'false') {
                value = false;
            }
            if (value === 'true') {
                value = true;
            }
            obj.filters[key] = value;
        }
        return obj;
    }, {});
    return queryStringState;
};
function handleHistoryBack(search, actions, tableDefaultState) {
    // Ottengo lo stato dalla querystring
    const changeQsState = getStateFromQs(search);
    // Se lo stato estratto è vuoto allora resetto i filtri
    if (isEmpty(changeQsState)) {
        actions.resetFilters({ pageSize: tableDefaultState.pageSize });
    }
    else {
        // Altrimenti mergio lo stato precedente con quanto estratto
        const newState = Object.assign(Object.assign(Object.assign({}, tableDefaultState), omit(changeQsState, 'filters')), { filters: Object.assign(Object.assign({}, tableDefaultState.filters), changeQsState.filters) });
        actions.replaceState(newState);
    }
}
function getQueryStringFromDiff(diff) {
    // Trasformo lo stato in maniera che le chiavi dei filtri si trovino nella root dell'oggetto
    const flatState = transform(diff, (obj, value, key) => {
        if (key !== 'filters') {
            obj[key] = value;
            if (key === 'sortBy') {
                obj[key] = value.map((entry) => `${entry.id}::${entry.desc ? 'desc' : 'asc'}`);
            }
        }
        else {
            const pairs = Object.entries(value);
            pairs.forEach(([k, v]) => {
                obj[k] = v;
            });
        }
        return obj;
    }, {});
    return queryString.stringify(flatState, {
        skipEmptyString: true,
        skipNull: true,
        encode: false,
    });
}
function useUrlSyncedDataTableState(overrides, options = {}) {
    const history = useHistory();
    const location = useLocation();
    defaults(options, {
        useOverridesAsBase: false,
        defaultPageSize: 20,
    });
    const COMPUTED_DEFAULT_STATE = Object.assign(Object.assign({}, DEFAULT_STATE), { pageSize: options.defaultPageSize });
    const computedOverrides = useMemo(() => (Object.assign(Object.assign({}, overrides), { pageSize: options.defaultPageSize })), [overrides, options.defaultPageSize]);
    const firstUpdate = useRef(true);
    const lastSearchRef = useRef('');
    // Mi serve tenere traccia dell'ultimo queryString per cui sono stati applicati i filtri
    const [lastSearch, setLastSearch] = useState(location.search);
    // Calcolo e mantengo memorizzato lo stato derivato dalla querystring al primo caricamento della tabella
    const initialQsState = useMemo(() => {
        // console.log(location.search);
        return getStateFromQs(location.search);
        /* eslint-disable-next-line */
    }, []);
    const initialReducerState = useMemo(() => {
        // console.log(initialQsState);
        // console.log(overrides);
        // Se la query string è vuota, ritorno direttamente gli overrides (la querystring verrà nello useEffect successivo, se useOverridesAsBase = false)
        if (Object.keys(initialQsState).length === 0) {
            return computedOverrides;
        }
        // console.log(JSON.stringify(initialQsState));
        // Se nella querystring è specificato un ordinamento o se negli overrides non è specificato ritorno direttamente quanto contenuto nella querystring
        if (initialQsState.sortBy || overrides.sortBy === null) {
            return initialQsState;
        }
        // Altrimenti creo un nuovo stato composto da filtri della querystring e ordinamento degli overrides, aggiornando l'entry nell'history
        const reducerState = Object.assign(Object.assign({}, initialQsState), { sortBy: overrides.sortBy || [] });
        const diff = difference(reducerState, COMPUTED_DEFAULT_STATE);
        // console.log(diff);
        // Genero il nuovo querystring e lo pusho nell'history del browser
        const search = getQueryStringFromDiff(diff);
        // console.log(search);
        setTimeout(() => {
            history.replace(`${location.pathname}?${search}`);
        }, 0);
        return reducerState;
        // return merge({}, overrides, initialQsState);
        /* eslint-disable-next-line */
    }, []);
    // console.log(initialQsState);
    // console.log(initialReducerState);
    // Inizializzo il reducer mergiando lo stato estratto dalla querystring con quello fornito
    const [state, dispatch] = useReducer(tableFilterReducer, initialReducerState, (state) => defaults(state, COMPUTED_DEFAULT_STATE));
    const actions = useMemo(() => bindActions(dispatch, computedOverrides), [computedOverrides]);
    // Mi salvo lo stato di default della tabella in maniera da poter calcolare le differenze da riflettere nella querystring
    const tableDefaultState = useMemo(() => {
        return defaults({}, computedOverrides, COMPUTED_DEFAULT_STATE);
        /* eslint-disable-next-line */
    }, []);
    useEffect(() => {
        if (options.useOverridesAsBase === false) {
            if (location.search === '') {
                const diff = difference(tableDefaultState, COMPUTED_DEFAULT_STATE);
                // Genero il nuovo querystring e lo pusho nell'history del browser
                const search = getQueryStringFromDiff(diff);
                setLastSearch(`?${search}`);
                lastSearchRef.current = `?${search}`;
                history.replace(`${location.pathname}?${search}`);
            }
        }
        // Ascolto i cambi di location
        return history.listen((changeLocation, action) => {
            var _a, _b;
            // console.log(action);
            // console.log(changeLocation);
            // In caso di azione di tipo "POP", ovvero torno indietro
            if (action === 'POP') {
                // Aggiorno il lastSearch
                setLastSearch(changeLocation.search);
                lastSearchRef.current = changeLocation.search;
                handleHistoryBack(changeLocation.search, actions, COMPUTED_DEFAULT_STATE);
            }
            else if (lastSearchRef.current !== changeLocation.search) {
                if ((_a = changeLocation === null || changeLocation === void 0 ? void 0 : changeLocation.state) === null || _a === void 0 ? void 0 : _a.tabuliskLink) {
                    return;
                }
                if ((_b = changeLocation === null || changeLocation === void 0 ? void 0 : changeLocation.state) === null || _b === void 0 ? void 0 : _b.tabuliskDefaultLink) {
                    const diff = difference(computedOverrides, COMPUTED_DEFAULT_STATE);
                    const defaultQs = getQueryStringFromDiff(diff);
                    lastSearchRef.current = changeLocation.search;
                    setLastSearch(`?${defaultQs}`);
                    history.replace(`${location.pathname}?${defaultQs}`);
                    handleHistoryBack(defaultQs, actions, COMPUTED_DEFAULT_STATE);
                    return;
                }
                lastSearchRef.current = changeLocation.search;
                handleHistoryBack(changeLocation.search, actions, COMPUTED_DEFAULT_STATE);
            }
        });
        /* eslint-disable-next-line */
    }, []); // Eseguo solo al primo render
    useEffect(() => {
        // console.warn('EFFECT');
        if (firstUpdate.current) {
            firstUpdate.current = false;
            if (options.useOverridesAsBase === false) {
                return;
            }
        }
        // Ad ogni render calcolo le differenze tra stato attuale e stato di default
        const diff = difference(state, options.useOverridesAsBase ? tableDefaultState : COMPUTED_DEFAULT_STATE);
        const search = getQueryStringFromDiff(diff);
        // Controllo necessario per evitare che venga pushato nell'history un path
        // uguale al precedente al primo rendering quando il search dovrebbe essere vuoto
        // -> (search !== '' || lastSearch !== '')
        const searchString = `?${search}`;
        if ((search !== '' || lastSearch !== '') &&
            lastSearch !== searchString &&
            lastSearchRef.current !== searchString) {
            setLastSearch(searchString);
            lastSearchRef.current = searchString;
            history.push(`${location.pathname}?${search}`);
        }
        /* eslint-disable-next-line */
    }, [state]);
    // const remoteDataTableProps = getRemoteDataTableProps(state, actions);
    return useMemo(() => ({ state, actions, dispatch }), [state, actions]);
}
const actionTypes = {
    SET_PAGE,
    SET_PAGE_SIZE,
    SET_FILTER,
    SET_GLOBAL_FILTER,
    TOGGLE_SORT_BY,
    RESET_FILTER,
    RESET_FILTERS,
    REPLACE_STATE,
};

const CreateEditableTableCell = (
// onBlur: Function,
idProperty = 'id', inputClassName = '') => {
    const EditableCell = (props) => {
        const originalValue = useMemo(() => props.value, [props.value]);
        const [value, setValue] = useState(props.value);
        const [isUpdating, setIsUpdating] = useState(false);
        const onChange = (e) => {
            setValue(e.target.value);
        };
        // Handle external changes
        useEffect(() => {
            setValue(props.value);
        }, [props.value]);
        return (React.createElement("input", { className: inputClassName, value: value || '', onChange: onChange, disabled: isUpdating, "data-tabulisk-editable": `editable_${props.column.id}_${props.row.original[idProperty]}`, onKeyUp: async (e) => {
                if (e.keyCode === 13) {
                    // ENTER
                    if (value !== originalValue) {
                        setIsUpdating(true);
                        const res = await props.updateData(props.row.original[idProperty], props.column.id, value);
                        setIsUpdating(false);
                        if (res === false) {
                            setValue(originalValue);
                        }
                    }
                }
                else if (e.keyCode === 27) {
                    // ESC
                    setValue(originalValue);
                }
            }, onBlur: () => {
                if (value !== originalValue) {
                    setValue(originalValue);
                    // props.updateData(props.row.original[idProperty], props.column.id, value);
                }
            } }));
    };
    return EditableCell;
};
/*export const useEditableTable = (originalData: any, onUpdateData: Function, idProperty = 'id') => {
    const [tableData, setData] = useState(originalData);
    const [skipAutoReset, setSkipAutoReset] = useState(false);

    const updateDataFn = useCallback(
        async (id: any, columnId: string, value: any) => {
            setSkipAutoReset(true);

            const res = await onUpdateData(id, columnId, value);

            setData((old: any) =>
                old.map((row: any) => {
                    if (String(id) === String(row[idProperty])) {
                        return res;
                    }

                    return row;
                })
            );
        },
        [onUpdateData, idProperty]
    );

    // Handle external changes
    React.useEffect(() => {
        setData(originalData);
    }, [originalData]);

    React.useEffect(() => {
        setSkipAutoReset(false);
    }, [tableData]);

    return {
        tableData,
        skipAutoReset,
        updateDataFn,
    };
};*/

export { CreateEditableTableCell, DataTable, TabuliskConfigProvider, actionTypes, handleHistoryBack, initTableFilterState, tableFilterReducer, useDataTableState, useTabuliskConfig, useUrlSyncedDataTableState };
